import {
	SecuritySettings,
	TokenStorageMode,
	cacheSecuritySettings,
	readCachedSecuritySettings
} from './securitySettings';
import { buildContentSecurityPolicy } from './csp';

/**
 * Applies the runtime security settings, as early as the app can.
 *
 * "As early as" is the whole difficulty. Where Cognito tokens are kept has to be
 * decided before Amplify's Auth module is configured, and a policy is worth most
 * before anything has been fetched — neither can wait for a round trip. So this
 * runs off the cached settings, synchronously, and the fetch that follows only
 * feeds the cache for the next load.
 *
 * Consequence, stated plainly: a change Ops make reaches a browser on its second
 * load. Rolling back a policy that broke a screen is one reload for the reader
 * and no deploy for anyone, which is the property that was asked for.
 */

/** Marks the meta element, so a second call cannot stack a second policy. */
const CSP_ELEMENT_ID = 'pearsana-csp';

/**
 * The Amplify config the policy's origins are derived from, remembered here at
 * boot.
 *
 * Held rather than imported where it is needed, because `aws-exports` is
 * generated per environment and is not in the repository: a module that imports
 * it cannot be loaded anywhere the file has not been generated, which includes
 * CI. index.tsx has it and hands it over; everything else asks for it here.
 *
 * Empty until the bootstrap runs, which yields a policy with no AWS origins —
 * fine, because nothing applies a policy before the bootstrap has run.
 */
let amplifyConfig: Record<string, unknown> = {};

/**
 * The key prefixes Amplify and the Cognito SDK use.
 *
 * Needed because switching storage mode leaves tokens behind in the store that
 * is no longer active — and the first mode anyone will switch away from is
 * localStorage, which is precisely where we did not want them left. So the
 * inactive stores are emptied of these keys on every boot.
 */
const AUTH_KEY_PREFIXES = [
	'CognitoIdentityServiceProvider.',
	'amplify-signin-with-hostedUI',
	'amplify-redirected-from-hosted-ui',
	'aws.cognito.identity-id.',
	'aws.cognito.identity-providers.'
];

const isAuthKey = (key: string): boolean => AUTH_KEY_PREFIXES.some((prefix) => key.startsWith(prefix));

/** The store for a mode, or undefined to leave Amplify's own default alone. */
export const resolveTokenStorage = (mode: TokenStorageMode): Storage | undefined => {
	try {
		if (mode === 'session') {
			return window.sessionStorage;
		}
	} catch {
		// Storage can throw outright in a locked-down browser. Amplify's default is
		// the right fallback: a signed-out user is better than a blank app.
		return undefined;
	}
	return undefined;
};

/** Empties the stores that are not carrying tokens this session. */
export const purgeInactiveTokenStores = (mode: TokenStorageMode): void => {
	const inactive: Storage[] = [];
	try {
		if (mode !== 'local') {
			inactive.push(window.localStorage);
		}
		if (mode !== 'session') {
			inactive.push(window.sessionStorage);
		}
	} catch {
		return;
	}

	inactive.forEach((store) => {
		try {
			const keys: string[] = [];
			for (let index = 0; index < store.length; index += 1) {
				const key = store.key(index);
				if (key && isAuthKey(key)) {
					keys.push(key);
				}
			}
			keys.forEach((key) => store.removeItem(key));
		} catch {
			// Nothing to clean if the store cannot be read.
		}
	});
};

/**
 * Inserts the policy, once. Returns whether a policy is now in force.
 *
 * Development is not exempt, deliberately. It used to be, on the grounds that
 * the dev bundle needs inline script and eval — which is not true of this one:
 * CRA 5 builds it with `cheap-module-source-map`, and loading the app under its
 * own policy produces no refusal the dev server is responsible for.
 *
 * The exemption was not free. It meant the policy was first exercised in a
 * deployed environment, and a meta policy cannot be report-only, so the first
 * sign of a directive being too narrow was a broken screen in front of someone.
 * It hid exactly that: two missing Superchat hosts that left the widget unable
 * to open a conversation wherever the policy was on. Development now obeys
 * `cspMode` like every other environment rather than ignoring it.
 *
 * Applying it late — after the fetch, on a browser whose cache said `off` — is
 * still worth doing: the Superchat script is injected from an effect, so it is
 * loaded under whatever policy is in place by then.
 */
export const applyContentSecurityPolicy = (
	settings: SecuritySettings,
	config: Record<string, unknown>,
	documentRef: Document = document
): boolean => {
	if (documentRef.getElementById(CSP_ELEMENT_ID)) {
		return true;
	}
	if (settings.cspMode !== 'on') {
		return false;
	}

	const meta = documentRef.createElement('meta');
	meta.id = CSP_ELEMENT_ID;
	meta.setAttribute('http-equiv', 'Content-Security-Policy');
	meta.setAttribute('content', buildContentSecurityPolicy(settings, config));
	documentRef.head.appendChild(meta);
	return true;
};

/**
 * The policy the browser is enforcing on this page, or null if there is none.
 *
 * Read off the element rather than recomputed, because the two can disagree and
 * the disagreement is the useful part: a page carries the policy its cache held
 * when it loaded, so a setting saved since is not in force here yet.
 */
export const policyInForce = (documentRef: Document = document): string | null =>
	documentRef.getElementById(CSP_ELEMENT_ID)?.getAttribute('content') ?? null;

/**
 * Whether the browser will refuse `eval` on this page.
 *
 * Asked by anything that would otherwise probe for eval and get reported for it
 * — pdf.js does exactly that, and every PDF a reader opened used to leave a
 * refusal in the console and in RUM.
 *
 * It reads the element rather than the setting, because those two disagree in
 * both directions and only the element is the truth here. A page that loaded
 * before `cspMode` was turned on carries no policy and eval works; a page that
 * loaded while it was on keeps the policy after it is turned off, because a meta
 * policy cannot be withdrawn from a document that already has one.
 *
 * The inference from "a policy is in force" to "eval is refused" holds because
 * this app's builder never emits `'unsafe-eval'` and the origins an operator can
 * add are validated to be https addresses, so no setting can introduce it. If
 * that ever changed, this would be conservative rather than wrong — it would
 * decline a fast path that was in fact available.
 */
export const evalIsRefused = (documentRef: Document = document): boolean => policyInForce(documentRef) !== null;

/**
 * The policy a next page load would apply for these settings, or null for none.
 *
 * Exists so the admin screen can show the policy without knowing anything about
 * `aws-exports`: the origins are derived from the config this module was handed
 * at boot, which is the only copy of it the app has.
 */
export const policyForSettings = (settings: SecuritySettings): string | null =>
	settings.cspMode === 'on' ? buildContentSecurityPolicy(settings, amplifyConfig) : null;

/**
 * Reads the cached settings and acts on them. Called before Amplify is
 * configured; returns the storage Amplify's Auth module should be given.
 */
export const bootstrapSecurity = (
	config: Record<string, unknown>
): {
	settings: SecuritySettings;
	storage: Storage | undefined;
} => {
	amplifyConfig = config;
	const settings = readCachedSecuritySettings();
	applyContentSecurityPolicy(settings, config);
	purgeInactiveTokenStores(settings.tokenStorage);
	return { settings, storage: resolveTokenStorage(settings.tokenStorage) };
};

/**
 * Records freshly fetched settings, and applies a policy if the cache had not
 * told us to. A storage change is deliberately not acted on here: moving tokens
 * mid-session would sign the reader out under their own hands, so it waits for
 * the next load.
 */
export const reconcileSecuritySettings = (
	settings: SecuritySettings,
	config: Record<string, unknown> = amplifyConfig
): void => {
	cacheSecuritySettings(settings);
	applyContentSecurityPolicy(settings, config);
};
